import { useMutation, type ApolloError } from '@apollo/client'
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  ACTIVATE_DEFERRED_PROMOTION,
  PromotionSelectionCriteria,
  type DeferredPromotionCriteria,
  type IActivateDeferredPromotion,
  type IActivateDeferredPromotionVariables,
} from '@commerce/core'

export interface ActivateDeferredPromotionArgs {
  promotionUid: string
  selectBy: DeferredPromotionCriteria
  /**
   * The chosen group's own `identifier`, echoed back untouched. Required by
   * `CUSTOMER_SELECTION`; there is nothing to send under `DEFAULT_GROUP_ID`,
   * where the server already knows which reward applies.
   */
  selectedGroupIdentifier?: string
}

export interface UseActivateDeferredPromotion {
  /** Resolves true when the cart came back changed, false when it did not. */
  activate: (args: ActivateDeferredPromotionArgs) => Promise<boolean>
  /**
   * The promotions currently in flight, by uid.
   *
   * A set rather than a boolean because the cards are independent: two
   * promotions can be claimed at once, and a single flag would re-enable the
   * first card the moment the second was pressed. It is also what makes a slow
   * reply harmless — see `activate`.
   */
  activatingUids: ReadonlySet<string>
  error: ApolloError | undefined
}

/**
 * Claims a promotion the cart has already earned.
 *
 * No `refetchQueries`: `activateDeferredPromotion` answers with the whole
 * `Cart`, so Apollo writes it onto the normalised `Cart:<id>` entity and the
 * lines, the totals and the remaining deferred promotions all repaint together
 * — the same mechanism `cartAddProduct` and `prefillCart` rely on.
 *
 * `onError` is not decoration: without a handler Apollo rejects the promise
 * `mutate` returns. With it, a failure resolves with no data, which is why this
 * reports success by looking at the payload rather than at the absence of a
 * throw.
 */
export const useActivateDeferredPromotion = (): UseActivateDeferredPromotion => {
  const { slug = '', sessionId = '' } = useParams()
  const [activatingUids, setActivatingUids] = useState<ReadonlySet<string>>(new Set())
  const [mutate, { error }] = useMutation<
    IActivateDeferredPromotion,
    IActivateDeferredPromotionVariables
  >(ACTIVATE_DEFERRED_PROMOTION, { onError: () => undefined })

  const activate = useCallback(
    async ({
      promotionUid,
      selectBy,
      selectedGroupIdentifier,
    }: ActivateDeferredPromotionArgs): Promise<boolean> => {
      // A customer selection is *only* the identifier. Sending the criterion
      // without it would ask the server to pick, which is the opposite of what
      // the shopper was offered.
      if (
        selectBy === PromotionSelectionCriteria.CUSTOMER_SELECTION &&
        !selectedGroupIdentifier
      ) {
        return false
      }

      setActivatingUids((pending) => new Set(pending).add(promotionUid))

      try {
        const result = await mutate({
          variables: {
            storefrontName: slug,
            sessionUid: sessionId,
            data: {
              promotionUid,
              selectBy,
              ...(selectedGroupIdentifier ? { selectedGroupIdentifier } : {}),
            },
          },
        })

        return Boolean(result?.data?.activateDeferredPromotion)
      } catch {
        // `onError` absorbs GraphQL and network failures, so reaching here means
        // something else threw — a cache-write invariant, say. Reported as a
        // refused claim rather than rejected: the call site is `void activate(…)`,
        // where a rejection becomes an unhandled one instead of the error alert.
        return false
      } finally {
        // In `finally` because a throw would otherwise leave the uid in the set
        // for the life of the page: the card would sit disabled behind a spinner
        // with nothing left to clear it.
        //
        // Keyed by *which* promotion answered, not by "something answered": a
        // reply for a card the shopper has since left has no business clearing
        // the spinner on the one they are looking at now.
        setActivatingUids((pending) => {
          if (!pending.has(promotionUid)) return pending
          const next = new Set(pending)
          next.delete(promotionUid)
          return next
        })
      }
    },
    [mutate, slug, sessionId],
  )

  return { activate, activatingUids, error }
}
